import { useCallback, useEffect, useRef, useState } from 'react';
import { MAX_TEXT_CHARS } from './protocol';
import type {
  ClientCapability,
  ClientMessage,
  ModelRef,
  ServerMessage,
  SessionContext,
  SessionCredentials,
  ThinkingLevel,
  UserLocation,
} from './protocol';
import type { ClientErrorCode } from './errorCopy';
import { sessionStartFrame, userMessageFrame } from './frames';
import {
  CREDENTIALS_FAILED_REASON,
  initialState,
  isRecoverableClose,
  piChatReducer,
  selectIsBusy,
  type PiChatAction,
} from './reducer';

const RECONNECT_DELAYS_MS = [0, 1000, 2000, 4000];

type Recovery = 'idle' | 'resume' | 'retry';

interface PiChatStartBase {
  url: string;
  context: SessionContext;
  profile?: string;
  capabilities?: ClientCapability[];
  model?: ModelRef;
  thinkingLevel?: ThinkingLevel;
}

export type PiChatStart = PiChatStartBase &
  (
    | { conversationId?: string; newConversation?: never }
    | { conversationId?: never; newConversation?: true }
  );

export interface PiChatFrameMeta {
  url: string;
  signal: AbortSignal;
  isCurrent: () => boolean;
}

export type GetCredentials = (
  purpose: 'session.start' | 'user.message'
) => Promise<SessionCredentials>;

export interface UsePiChatOptions {
  getCredentials: GetCredentials;
  credentialsErrorNotice: string;
  onServerMessage?: (message: ServerMessage, meta: PiChatFrameMeta) => void;
  isSendBlocked?: () => boolean;
  getLocation?: () => UserLocation | undefined;
}

export const usePiChat = ({
  getCredentials,
  credentialsErrorNotice,
  onServerMessage,
  isSendBlocked,
  getLocation,
}: UsePiChatOptions) => {
  const [state, setState] = useState(initialState);
  const stateRef = useRef(initialState);
  const dispatch = useCallback((action: PiChatAction) => {
    stateRef.current = piChatReducer(stateRef.current, action);
    setState(stateRef.current);
  }, []);
  const socketRef = useRef<WebSocket | null>(null);
  const getCredentialsRef = useRef(getCredentials);
  getCredentialsRef.current = getCredentials;
  const credentialsErrorNoticeRef = useRef(credentialsErrorNotice);
  credentialsErrorNoticeRef.current = credentialsErrorNotice;
  const onServerMessageRef = useRef(onServerMessage);
  onServerMessageRef.current = onServerMessage;
  const isSendBlockedRef = useRef(isSendBlocked);
  isSendBlockedRef.current = isSendBlocked;
  const getLocationRef = useRef(getLocation);
  getLocationRef.current = getLocation;
  const connectionAbortRef = useRef<AbortController | null>(null);
  const endConnection = useCallback(() => {
    connectionAbortRef.current?.abort();
    connectionAbortRef.current = null;
  }, []);
  const getStartRef = useRef<() => PiChatStart>();
  const recoveryRef = useRef<Recovery | null>(null);
  const attemptRef = useRef(0);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingRef = useRef<{
    text: string;
    settle: (sent: boolean) => void;
  } | null>(null);

  const stopRecovery = useCallback(() => {
    clearTimeout(retryTimerRef.current);
    recoveryRef.current = null;
    attemptRef.current = 0;
  }, []);

  const dropPending = useCallback(() => {
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.settle(false);
  }, []);

  const giveUp = useCallback(
    (reason: string) => {
      stopRecovery();
      dispatch({ type: 'socketClosed', reason });
      dropPending();
    },
    [stopRecovery, dispatch, dropPending]
  );

  const send = useCallback((message: ClientMessage) => {
    const ws = socketRef.current;
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
      return true;
    }
    return false;
  }, []);

  const messageCredentials = useCallback(
    async (ws: WebSocket | null): Promise<SessionCredentials | null> => {
      try {
        return await getCredentialsRef.current('user.message');
      } catch {
        if (socketRef.current === ws) {
          dispatch({
            type: 'clientNotice',
            text: credentialsErrorNoticeRef.current,
            isError: true,
          });
        }
        return null;
      }
    },
    [dispatch]
  );

  const sendNow = useCallback(
    async (trimmed: string): Promise<boolean> => {
      const ws = socketRef.current;
      const sendable = () =>
        socketRef.current === ws &&
        ws?.readyState === WebSocket.OPEN &&
        !isSendBlockedRef.current?.();
      if (!sendable()) return false;
      const credentials = await messageCredentials(ws);
      if (!credentials || !sendable()) return false;
      const clientMessageId = crypto.randomUUID();
      if (
        !send(
          userMessageFrame({
            text: trimmed,
            clientMessageId,
            credentials,
            location: getLocationRef.current?.(),
          })
        )
      ) {
        return false;
      }
      dispatch({ type: 'userSent', text: trimmed, clientMessageId });
      return true;
    },
    [send, messageCredentials, dispatch]
  );

  const openSocket = useCallback(
    function open(start: PiChatStart) {
      const { url } = start;
      const previous = socketRef.current;
      socketRef.current = null;
      previous?.close(1000);
      endConnection();

      let ws: WebSocket;
      try {
        ws = new WebSocket(url);
      } catch (error) {
        console.warn('AI chat socket could not be created', error);
        dispatch({ type: 'socketError' });
        giveUp('connect_failed');
        return;
      }
      socketRef.current = ws;
      const connectionAbort = new AbortController();
      connectionAbortRef.current = connectionAbort;
      const meta: PiChatFrameMeta = {
        url,
        signal: connectionAbort.signal,
        isCurrent: () =>
          socketRef.current === ws && ws.readyState === WebSocket.OPEN,
      };

      ws.onopen = () => {
        if (socketRef.current !== ws) return;
        getCredentialsRef.current('session.start').then(
          (credentials) => {
            if (socketRef.current !== ws || ws.readyState !== WebSocket.OPEN) {
              return;
            }
            ws.send(JSON.stringify(sessionStartFrame(start, credentials)));
          },
          () => {
            if (socketRef.current !== ws) return;
            socketRef.current = null;
            ws.close(1000);
            endConnection();
            giveUp(CREDENTIALS_FAILED_REASON);
          }
        );
      };
      ws.onmessage = (event) => {
        if (socketRef.current !== ws) return;
        const message: ServerMessage = JSON.parse(event.data);
        dispatch({ type: 'server', message });
        onServerMessageRef.current?.(message, meta);
        if (message.type === 'session.ready') {
          stopRecovery();
          const pending = pendingRef.current;
          pendingRef.current = null;
          if (pending) void sendNow(pending.text).then(pending.settle);
        } else if (stateRef.current.connectionState === 'closed') {
          stopRecovery();
          dropPending();
        }
      };
      ws.onclose = (event) => {
        if (socketRef.current !== ws) return;
        socketRef.current = null;
        endConnection();
        const reason = event.reason || `socket closed (${event.code})`;
        const current = stateRef.current;
        const recoverable = isRecoverableClose(current, reason);
        const wasReady = current.connectionState === 'ready';
        if (
          recoverable &&
          (recoveryRef.current === 'retry' ||
            (wasReady && selectIsBusy(current)))
        ) {
          if (attemptRef.current < RECONNECT_DELAYS_MS.length) {
            recoveryRef.current = 'retry';
            const delay = RECONNECT_DELAYS_MS[attemptRef.current];
            attemptRef.current += 1;
            dispatch({ type: 'reconnecting' });
            retryTimerRef.current = setTimeout(
              () => open(getStartRef.current!()),
              delay
            );
            return;
          }
        } else if (recoverable && wasReady) {
          recoveryRef.current = 'idle';
          dispatch({ type: 'idle' });
          return;
        }
        giveUp(reason);
      };
    },
    [endConnection, dispatch, giveUp, stopRecovery, dropPending, sendNow]
  );

  const connect = useCallback(
    (getStart: () => PiChatStart) => {
      stopRecovery();
      getStartRef.current = getStart;
      dispatch({ type: 'connecting' });
      openSocket(getStart());
    },
    [stopRecovery, dispatch, openSocket]
  );

  const resume = useCallback(() => {
    if (recoveryRef.current !== 'idle') return;
    recoveryRef.current = 'resume';
    dispatch({ type: 'reconnecting' });
    openSocket(getStartRef.current!());
  }, [dispatch, openSocket]);

  const disconnect = useCallback(() => {
    const ws = socketRef.current;
    socketRef.current = null;
    ws?.close(1000);
    endConnection();
    giveUp('client');
  }, [endConnection, giveUp]);

  const sendMessage = useCallback(
    (text: string): Promise<boolean> => {
      const trimmed = text.trim().slice(0, MAX_TEXT_CHARS);
      if (!trimmed) return Promise.resolve(false);
      if (recoveryRef.current === null) return sendNow(trimmed);
      if (pendingRef.current) return Promise.resolve(false);
      const sent = new Promise<boolean>((settle) => {
        pendingRef.current = { text: trimmed, settle };
      });
      resume();
      return sent;
    },
    [sendNow, resume]
  );

  const restoreSnapshot = useCallback(
    async (snapshotId: string): Promise<boolean> => {
      const ws = socketRef.current;
      const sendable = () =>
        socketRef.current === ws && ws?.readyState === WebSocket.OPEN;
      if (!sendable()) return false;
      const credentials = await messageCredentials(ws);
      if (!credentials || !sendable()) return false;
      return send({ type: 'snapshot.restore', snapshotId, credentials });
    },
    [send, messageCredentials]
  );

  const addNotice = useCallback(
    (notice: string | { code: ClientErrorCode }, isError = true) =>
      dispatch({
        type: 'clientNotice',
        isError,
        ...(typeof notice === 'string' ? { text: notice } : notice),
      }),
    [dispatch]
  );

  const abort = useCallback(() => send({ type: 'session.abort' }), [send]);

  useEffect(
    () => () => {
      const ws = socketRef.current;
      socketRef.current = null;
      ws?.close(1000);
      endConnection();
      stopRecovery();
      dropPending();
    },
    [endConnection, stopRecovery, dropPending]
  );

  return {
    ...state,
    connect,
    resume,
    disconnect,
    sendMessage,
    restoreSnapshot,
    addNotice,
    abort,
    send,
  };
};
